/**
 * How the 2.5D renderer draws an authored layout's buildings (docs/archive/YANYU.md §9.8) with the sprites
 * it already has: each building type is drawn as its structure kind (`BASE_KIND`, the nearest one
 * that has a sprite), on a square footprint centred in the building's own, since the sprites are
 * square. Not the final look: the 3D renderer draws the types as themselves.
 */
import type { BuildingType, Structure } from '@nine/shared';
import { kitDraws } from './kit';

/** Red-roofed types (the shrine hall, the hall of the six schools) take the house's red variant. */
const RED: ReadonlySet<BuildingType> = new Set<BuildingType>(['hall', 'recruit']);

/**
 * A structure as the 2.5D sprites draw it: its kind on a square footprint centred in the authored
 * one (gates keep their span along their beam); the structure itself when it is already square or
 * not authored. Null for a type the 2.5D does not draw (an arena: its ring is its walls).
 */
export function structureLook(st: Structure): Structure | null {
  if (!st.type) return st;
  // Drawn by the plains kit (render/kit) on its full footprint.
  if (kitDraws(st)) return st;
  if (st.type === 'arena' || st.kind === 'terrace') return null;
  if (st.kind === 'gate') {
    const axis = st.axis ?? (st.w >= st.h ? 'x' : 'y');
    return axis === 'x'
      ? { ...st, axis, j: st.j + Math.floor(st.h / 2), h: 1 }
      : { ...st, axis, i: st.i + Math.floor(st.w / 2), w: 1 };
  }
  const n = Math.max(2, Math.min(st.w, st.h));
  return {
    ...st,
    i: st.i + Math.floor((st.w - n) / 2),
    j: st.j + Math.floor((st.h - n) / 2),
    w: n,
    h: n,
    variant: RED.has(st.type) ? 2 : st.variant % 3,
  };
}
